// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { productBrandStyles } from "@insite/content-library/Components/ProductBrand";
import { GridItemStyle } from "@insite/mobius/GridItem";
import productBrandVariantStyles from "@rpsc/App/SharedStyles/ProductBrandStyles";
import xProductPartNumbersStyles, { largePartNumberTextStyles } from "@rpsc/App/SharedStyles/ProductPartNumbersStyles";
import { viewStyles } from "@rpsc/Overrides/Widgets/ProductDetails/ProductDetailsView";
import { DivWrapper } from "@rpsc/XDL/constants";
import { fontConfig } from "@rpsc/XDL/PreStyle/typography";
import { color, hideElementOnPrint, xScreen } from "@rpsc/XDL/Utilities";
import { css } from "styled-components";

viewStyles.pageContainer = {
    css: css`
        display: flex;
        flex-direction: column;
        overflow: hidden;
        &&&& ${DivWrapper} {
            height: auto;
        }

        @media print {
            display: block;
        }
    `,
};

viewStyles.columnsContainer = {
    css: css`
        display: grid;
        grid-template-columns: minmax(0, auto) minmax(0, 378px);
        gap: 20px 22px;
        ${xScreen("lte", "lg")} {
            grid-template-columns: minmax(0, auto) minmax(0, 319px);
        }
        ${xScreen("lte", "md")} {
            display: flex;
            flex-wrap: wrap;
        }
        ${xScreen("lte", "md")} {
            display: flex;
            flex-wrap: wrap;
        }
        @media print {
            display: block;
        }
    `,
};

viewStyles.twoColumnsSection = {
    css: css`
        display: grid;
        grid-template-columns: minmax(0, 508px) 1fr;
        gap: 22px;
        width: 100%;

        ${xScreen("lte", "lg")} {
            grid-template-columns: minmax(0, 430px) 1fr;
        }
        ${xScreen("lte", "md")} {
            grid-template-columns: minmax(0, 358px) 1fr;
            margin-top: 8px;
        }
        ${xScreen("lte", "sm")} {
            display: flex;
            flex-wrap: wrap;
        }

        @media print {
            display: table !important;
            content: "";
            clear: both;
        }
    `,
};

viewStyles.primaryImageZone = {
    css: css`
        @media print {
            display: block;
            width: 50%;
            float: left;
        }
    `,
};

viewStyles.infoDescriptionSection = {
    css: css`
        display: flex;
        flex-direction: column;
        gap: 8px;
        flex: 1;
        margin-bottom: 12px;
        padding-bottom: 12px;
        ${xScreen("lte", "md")} {
            border-bottom: 1px solid ${color("common.border")};
        }

        @media print {
            display: block;
            width: 50%;
            border-bottom: 0;
            ${xScreen("lte", "md")} {
                float: none;
                width: 100%;
            }
        }
    `,
};

viewStyles.divider = {
    css: css`
        border-bottom: 1px solid ${color("common.border")};
        margin-block: 12px;
    `,
};

viewStyles.column = {
    css: css`
        display: flex;
        flex-direction: column;
        gap: 44px;
        width: 100%;
        @media print {
            display: block;
        }
    `,
};

viewStyles.rightColumn = {
    css: css`
        display: flex;
        flex-direction: column;
        gap: 24px;
        width: 378px;
        ${xScreen("lte", "lg")} {
            width: 319px;
        }
        ${xScreen("lte", "md")} {
            width: 100%;
        }
        ${xScreen("lte", "sm")} {
            gap: 20px;
        }

        @media print {
            -webkit-column-break-inside: avoid;
            page-break-inside: avoid;
            break-inside: avoid;
            float: left;
            display: block;
            width: 25%;
        }
    `,
};

viewStyles.productNumbersWrapper = {
    css: css`
        display: flex;
        align-items: baseline;
        flex-direction: column;
        width: 100%;

        @media print {
            display: block;
        }
    `,
};

viewStyles.productMyNumbersWrapper = {
    css: css`
        display: flex;
        gap: 8px;
        align-items: center;
        width: 100%;

        @media print {
            display: block;
        }
    `,
};

viewStyles.stickyWrapper = {
    css: css`
        position: fixed;
        left: 0;
        background: ${color("common.white")};
        width: 100%;
        z-index: 1300;
        transition: all 300ms ease-out;
        opacity: 1;
        min-height: 107px;
        height: auto;
        border-bottom: 1px solid ${color("common.border")};
        overflow: visible;

        &.stickyVisible {
            top: 0;
        }

        &.stickyHidden {
            top: -200px;
            &,
            & > * {
                max-height: 0;
                overflow: hidden;
            }
        }

        &.editingMode {
            position: unset;
        }

        ${hideElementOnPrint}
    `,
};

viewStyles.stickyPage = {
    css: css`
        &&&& {
            padding: 16px;
            margin-bottom: 0;
        }
    `,
};

viewStyles.bottomSection = {
    css: css`
        &:has(> ${DivWrapper}:empty) {
            display: none;
        }
        margin-top: 16px;
        ${hideElementOnPrint}
    `,
};

viewStyles.badgesContainer = {
    css: css`
        display: flex;
        gap: 4px;
        align-items: center;
        &:empty {
            display: none;
        }
        @media print {
            display: block;
        }
    `,
};

viewStyles.carouselsContainer = {
    css: css`
        ${hideElementOnPrint}
    `,
};

viewStyles.partNumbersStyles = {
    erpNumberGridItem: {
        width: 6,
    },
    customerNameGridItem: {
        width: 6,
    },
    manufacturerItemGridItem: {
        width: 6,
    },
    container: {
        offsetCss: css`
            align-items: center;
            ${xScreen("lte", "md")} {
                column-gap: 10px;
                max-width: 50%;
                > ${GridItemStyle} {
                    flex-basis: calc(50% - 10px);
                    &:last-child {
                        max-width: calc(50% - 10px);
                    }
                }
            }
            ${xScreen("lte", "sm")} {
                max-width: 100%;
            }
        `,
    },
    customerNameLabelText: {
        ...largePartNumberTextStyles,
        weight: fontConfig.weight.bold,
    },

    customerNameValueText: {
        ...largePartNumberTextStyles,
    },

    erpNumberLabelText: {
        ...largePartNumberTextStyles,
        weight: fontConfig.weight.bold,
    },

    erpNumberValueText: {
        ...largePartNumberTextStyles,
    },

    manufacturerItemLabelText: {
        ...largePartNumberTextStyles,
        weight: fontConfig.weight.bold,
    },

    manufacturerItemValueText: {
        ...largePartNumberTextStyles,
    },
};

viewStyles.brand = {
    nameLink: {
        ...productBrandVariantStyles?.medium?.nameLink,
    },
    nameText: {
        ...productBrandVariantStyles?.medium?.nameText,
    },
};
